import React, { useCallback, useEffect, useState } from "react";
import { faLocationDot, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { defaultImg } from "../../../../Constants/Images";
import moment from "moment";
import CustomButton from "../../../../Components/Button";
import { Table, Modal } from "react-bootstrap";
import { getDurationInMinutes, showToast } from "../../../../utils/helpers";

import {
  BookingItemType,
  SpaceItemType,
} from "../../../../Constants/Interfaces";
import useAuth from "../../../../Hooks/useAuth";
import {
  hostCheckInAction,
  hostCheckOutAction,
  userCheckInAction,
  userCheckOutAction,
} from "../../../../Services/Store/Actions/BookingsAction";
import { API } from "../../../../utils/Apis";
import { useDispatch } from "react-redux";
import {
  deleteRequestsItem,
  updateCollectedItem,
  updatePaidItem,
} from "../../../../Services/Store/Reducers/BookingsReducer";
import StripePaymentModal from "../../../../Components/Modals/StripePaymentModal";
import ReScheduleBooking from "../../../../Components/Modals/ReScheduleBooking";

interface BookingInfoPanelType {
  item: BookingItemType | null;
  listType: "collected" | "paid" | "boookingRequest";
  closePanel?: Function;
  showActionBtns?: "Yes" | "No";
}

const BookingInfoPanel: React.FC<BookingInfoPanelType> = ({
  item,
  listType,
  closePanel = null,
  showActionBtns = "Yes",
}) => {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [fetching, setFetching] = useState(true);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const [spaceImg, setSpaceImg] = useState(defaultImg);
  const [bookingItem, setBookingItem] = useState<BookingItemType | null>(null);
  const [space, setSpaceInfo] = useState<SpaceItemType | null>(null);

  const [checkInLoading, setCheckInLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [isBookingStarted, setIsBookingStarted] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isCheckedOut, setCheckedOut] = useState(false);
  const [isTimeExpired, setIsTimeExpired] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useState(false);

  // Host accept-with-price (custom bookings): lets the host accept at the
  // client's offer or counter with a different amount.
  const [acceptPrice, setAcceptPrice] = useState<{
    show: boolean;
    amount: string;
  }>({ show: false, amount: "" });

  const [timer, setTimer] = useState({
    start: false,
    duration: 0,
    remaining: 0,
  });

  const [duration, setDuration] = useState(0);
  const [showUpdateBooking, setUpdateBooking] = useState<{
    show: boolean;
    item: BookingItemType | null;
  }>({
    show: false,
    item: null,
  });

  const acceptCancelBooking = useCallback(
    (type = "accept", grossAmount?: number) => {
      console.log("bookingItem :", bookingItem);

      if (type === "accept") {
        if (!acceptLoading) {
          setAcceptLoading(true);

          const payload: any = {
            id: bookingItem.id,
            bookingId: bookingItem.id,
          };
          // Host counter-price (custom bookings only).
          if (grossAmount !== undefined && grossAmount !== null) {
            payload.grossAmount = grossAmount;
          }

          API.acceptRequest(payload)
            .then((res) => {
              console.log("RES :", res);

              if (res.type === "success") {
                dispatch(deleteRequestsItem({ id: bookingItem.id }));

                if (closePanel) {
                  closePanel();
                }
              }
              showToast(res.type, res.message);
            })
            .catch((err) => {
              //
              showToast("error", err.message);
            })
            .finally(() => setAcceptLoading(false));
        }
      } else if (type === "cancel") {
        if (!rejectLoading) {
          setRejectLoading(true);

          API.cancelRequest({ id: bookingItem.id, bookingId: bookingItem.id })
            .then((res) => {
              if (res.type === "success") {
                dispatch(deleteRequestsItem({ id: bookingItem.id }));

                if (closePanel) {
                  closePanel();
                }
              }
              showToast(res.type, res.message);
            })
            .catch((err) => {
              //
              showToast("error", err.message);
            })
            .finally(() => setRejectLoading(false));
        }
      }
    },
    [bookingItem]
  );

  const cancelBooking = useCallback(async () => {
    if (!cancelLoading) {
      const isConfirm = window.confirm(
        "Are you sure you want to cancel the booking?"
      );

      if (isConfirm) {
        setCancelLoading(true);

        API.cancelSpaceBooking({
          id: bookingItem && bookingItem.id ? bookingItem.id : "",
        })
          .then((res) => {
            console.log("Booking Cancel res :", res);

            if (res.type === "success") {
              if (listType === "collected") {
                dispatch(updateCollectedItem({ item: res.data }));
              } else if (listType === "paid") {
                dispatch(updatePaidItem({ item: res.data }));
              }

              // const updatedItem = { ...bookingItem, log: res.data };
              // if (res.type === "success" && listType === "collected") {
              //   dispatch(updateCollectedItem({ item: updatedItem }));
              // } else if (res.type === "success" && listType === "paid") {
              //   dispatch(updatePaidItem({ item: updatedItem }));
              // }
              showToast(res.type, res.message);

              if (closePanel) {
                setTimeout(() => {
                  closePanel();
                }, 500);
              }
            } else {
              showToast("error", res.message);
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setCancelLoading(false));
      }
    }
  }, [bookingItem, cancelLoading]);

  const spaceCheckIn = useCallback(() => {
    if (!checkInLoading) {
      setCheckInLoading(true);

      const params = {
        bookingId: bookingItem && bookingItem.id ? bookingItem.id : "",
        location: {
          lat: space && space.location ? space.location.coordinates[1] : 0,
          lng: space && space.location ? space.location.coordinates[0] : 0,
        },
      };

      if (bookingItem && bookingItem.clientId === user.id) {
        // User Check IN
        userCheckInAction(params)
          .then((res) => {
            const updatedItem = { ...bookingItem, log: res.data };

            setBookingItem(updatedItem);

            if (res.type === "success" && listType === "collected") {
              dispatch(updateCollectedItem({ item: updatedItem }));
            } else if (res.type === "success" && listType === "paid") {
              dispatch(updatePaidItem({ item: updatedItem }));
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setCheckInLoading(false));
      } else {
        // Host Check IN
        hostCheckInAction(params)
          .then((res) => {
            const updatedItem = { ...bookingItem, log: res.data };

            setBookingItem(updatedItem);

            if (res.type === "success" && listType === "collected") {
              dispatch(updateCollectedItem({ item: updatedItem }));
            } else if (res.type === "success" && listType === "paid") {
              dispatch(updatePaidItem({ item: updatedItem }));
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setCheckInLoading(false));
      }
    }
  }, [bookingItem, checkInLoading]);

  const spaceCheckOut = useCallback(() => {
    if (!checkInLoading) {
      setCheckInLoading(true);

      const params = {
        bookingId: bookingItem && bookingItem.id ? bookingItem.id : "",
        location: {
          lat: space && space.location ? space.location.coordinates[1] : 0,
          lng: space && space.location ? space.location.coordinates[0] : 0,
        },
      };

      if (bookingItem && bookingItem.clientId === user.id) {
        // User checkOut
        userCheckOutAction(params)
          .then((res) => {
            const updatedItem = {
              ...bookingItem,
              log: res.data,
              status: res.data.sratus,
            };

            if (res.type === "success" && listType === "collected") {
              dispatch(updateCollectedItem({ item: updatedItem }));
            } else if (res.type === "success" && listType === "paid") {
              dispatch(updatePaidItem({ item: updatedItem }));
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setCheckInLoading(false));
      } else {
        // Host checkOut
        hostCheckOutAction(params)
          .then((res) => {
            const updatedItem = { ...bookingItem, log: res.data };

            if (res.type === "success" && listType === "collected") {
              dispatch(updateCollectedItem({ item: updatedItem }));
            } else if (res.type === "success" && listType === "paid") {
              dispatch(updatePaidItem({ item: updatedItem }));
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => setCheckInLoading(false));
      }
    }
  }, [bookingItem, checkInLoading]);

  const getBookingDetails = useCallback(
    (id: string) => {
      API.getBookingDetails(id)
        .then((res) => {
          if (res.type === "success") {
            console.log("BookingItem :", res.data);

            const bookingItem = res.data;
            setSpaceInfo(bookingItem.space);
            setBookingItem(bookingItem);

            const duration = bookingItem
              ? getDurationInMinutes(
                  bookingItem.startDate,
                  bookingItem.startTime,
                  bookingItem.endDate,
                  bookingItem.endTime
                )
              : 0;

            setDuration(duration);

            if (bookingItem.space.media && bookingItem.space.media.length > 0) {
              const medias = bookingItem.space.media || [];
              for (let i = 0; i < medias.length; i++) {
                if (medias[i].type === "image") {
                  setSpaceImg(medias[i].url);
                  break;
                }
              }
            }
          }
        })
        .catch((err) => {
          console.log("Booking Details ERR :", err);
        })
        .finally(() => setFetching(false));
    },
    [item]
  );

  const updateCheckInOutStatus = useCallback(() => {
    if (bookingItem) {
      const currentDate = new Date();
      const startDate = new Date(
        `${bookingItem.startDate}T${bookingItem.startTime}:00`
      );
      const endDate = new Date(
        `${bookingItem.endDate}T${bookingItem.endTime}:00`
      );

      const duration = (endDate - startDate) / 1000;

      let remainingTime = 0;
      let timerStarted = false;

      if (startDate <= currentDate && currentDate < endDate) {
        remainingTime = (endDate - currentDate) / 1000;
        setIsBookingStarted(true);
        timerStarted = true;
      } else {
        remainingTime = (endDate - startDate) / 1000;
      }

      if (endDate < currentDate) {
        setIsTimeExpired(true);
        setIsBookingStarted(true);
      }

      if (
        bookingItem &&
        bookingItem.log &&
        (bookingItem.log.userCheckin.checkin ||
          bookingItem.log.hostCheckin.checkin)
      ) {
        setIsCheckedIn(true);
      }

      if (
        bookingItem &&
        bookingItem.log &&
        (bookingItem.log.hostCheckout.checkout ||
          bookingItem.log.userCheckout.checkout)
      ) {
        setCheckedOut(true);

        timerStarted = false;
      }

      setTimer({
        start: timerStarted,
        duration,
        remaining: remainingTime,
      });
    }
  }, [bookingItem]);

  useEffect(() => {
    updateCheckInOutStatus();
  }, [bookingItem]);

  useEffect(() => {
    if (timer.start || isTimeExpired || isCheckedOut) return; // 🧠 stop polling when timer starts

    const intervalId = setInterval(() => {
      updateCheckInOutStatus();
    }, 5000); // every 5 seconds

    return () => {
      console.log("Cleared Timeout...");

      clearInterval(intervalId);
    };
  }, [timer.start, updateCheckInOutStatus]);

  useEffect(() => {
    if (item) {
      getBookingDetails(item.id);
    }
  }, []);

  return (
    <div className="booking-info-wrapper">
      {fetching && (
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}

      {bookingItem && (
        <>
          <div className="space-infos">
            <div className="space-img">
              <img src={spaceImg} alt="" />
            </div>

            <div className="space-content">
              <h3>{space?.tagLine}</h3>
              <div className="d-flex align-items-center justify-content-between">
                <h2>{space?.name}</h2>
                <div className="space-price">${space?.ratePerHour}/h</div>
              </div>

              {space?.address && (
                <p>
                  <FontAwesomeIcon icon={faLocationDot} /> {space?.address}
                </p>
              )}

              <div className="row">
                <div className="col-md-6">
                  <div className="check-in-wrapper">
                    <label>Check In</label>
                    <p>
                      {bookingItem &&
                        moment(
                          `${bookingItem.startDate}T${bookingItem.startTime}:00`
                        ).format("DD MMM, YYYY")}
                    </p>
                    <p>
                      {bookingItem &&
                        moment(
                          `${bookingItem.startDate}T${bookingItem.startTime}:00`
                        ).format("hh:mm a")}
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="check-in-wrapper">
                    <label>Check Out</label>
                    <p>
                      {bookingItem &&
                        moment(
                          `${bookingItem.endDate}T${bookingItem.endTime}:00`
                        ).format("DD MMM, YYYY")}
                    </p>
                    <p>
                      {bookingItem &&
                        moment(
                          `${bookingItem.endDate}T${bookingItem.endTime}:00`
                        ).format("hh:mm a")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="transaction-infos">
            <Table>
              <tbody>
                <tr>
                  <th>Transaction ID</th>
                  <td>{bookingItem && bookingItem.id}</td>
                </tr>
                <tr>
                  <th>
                    Amount{" "}
                    <span className="info-text">
                      (${space?.ratePerHour} x {duration / 60} Hours)
                    </span>
                  </th>
                  <td>${bookingItem && bookingItem.grossAmount}</td>
                </tr>
                {bookingItem &&
                  bookingItem.type === "custom" &&
                  bookingItem.offeredAmount !== null &&
                  bookingItem.offeredAmount !== undefined && (
                    <tr>
                      <th>Offered Amount</th>
                      <td>${bookingItem.offeredAmount}</td>
                    </tr>
                  )}
                <tr>
                  <th>Requested Date</th>
                  <td>
                    {bookingItem &&
                      moment(bookingItem.createdAt).format(
                        "DD MMM YYYY, hh:MM a"
                      )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {showActionBtns === "Yes" && (
            <div className="action-btns-wrapper">
              {bookingItem.status === "cancelled" && (
                <CustomButton
                  title="Cancelled"
                  onClick={() => {}}
                  disabled={true}
                />
              )}

              {bookingItem.status === "payment-pending" &&
                bookingItem.type === "custom" && (
                  <CustomButton
                    title="Make Payment"
                    onClick={() => togglePaymentModal(true)}
                  />
                )}

              {bookingItem.status === "request-pending" && (
                <>
                  <CustomButton
                    isLoading={acceptLoading}
                    title="Accept Booking"
                    onClick={() => {
                      // Custom bookings are an offer — let the host confirm or
                      // counter the amount before accepting. Normal bookings
                      // have a fixed price, so accept directly.
                      if (bookingItem.type === "custom") {
                        setAcceptPrice({
                          show: true,
                          amount: String(
                            bookingItem.offeredAmount ??
                              bookingItem.grossAmount ??
                              ""
                          ),
                        });
                      } else {
                        acceptCancelBooking("accept");
                      }
                    }}
                  />

                  <CustomButton
                    isLoading={rejectLoading}
                    title="Cancel Booking"
                    onClick={() => acceptCancelBooking("cancel")}
                  />
                </>
              )}

              {bookingItem.status === "accepted" && (
                <>
                  {isTimeExpired && !isCheckedOut && (
                    <>
                      <CustomButton
                        isLoading={checkInLoading}
                        title="Booking Expired"
                        onClick={() => {
                          console.log("Clicked");
                        }}
                        disabled={true}
                      />

                      <span
                        className="as-link m-2 d-block"
                        onClick={() => spaceCheckOut()}
                      >
                        Mark as Complete
                      </span>
                    </>
                  )}

                  {isBookingStarted && !isTimeExpired && (
                    <CustomButton
                      isLoading={checkInLoading}
                      title={!isCheckedIn ? "Check In" : "Check Out"}
                      onClick={() => {
                        console.log("Clicked");

                        if (!isCheckedIn && !isTimeExpired) {
                          spaceCheckIn();
                        } else if (!isCheckedOut && !isTimeExpired) {
                          spaceCheckOut();
                        }
                      }}
                    />
                  )}

                  {listType === "paid" &&
                    !isTimeExpired &&
                    !isBookingStarted && (
                      <CustomButton
                        title="Update Booking"
                        onClick={() =>
                          setUpdateBooking({ show: true, item: bookingItem })
                        }
                      />
                    )}

                  {!isTimeExpired && !isBookingStarted && (
                    <CustomButton
                      isLoading={cancelLoading}
                      title="Cancel"
                      onClick={() => cancelBooking()}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {showUpdateBooking.show && (
        <ReScheduleBooking
          show={showUpdateBooking.show}
          item={showUpdateBooking.item}
          closeModal={() => setUpdateBooking({ show: false, item: null })}
          updateItem={(item: any) => {
            dispatch(updatePaidItem({ item: { ...bookingItem, ...item } }));

            setBookingItem({
              ...bookingItem,
              ...item,
            });
          }}
        />
      )}

      {showPaymentModal && (
        <StripePaymentModal
          show={showPaymentModal}
          onClose={() => togglePaymentModal(false)}
          bookingInfo={bookingItem}
          spaceItem={space}
          paymentInfo={(item && item.payment) || null}
          onSuccess={() => {
            getBookingDetails(item.id);

            dispatch(deleteRequestsItem({ id: item.id }));

            setTimeout(() => {
              togglePaymentModal(false);
              if (closePanel) {
                setTimeout(() => {
                  closePanel();
                }, 500);
              }
            }, 500);
          }}
        />
      )}

      {acceptPrice.show && (
        <Modal
          show={acceptPrice.show}
          onHide={() => setAcceptPrice({ show: false, amount: "" })}
          className="accept-price-modal"
        >
          <Modal.Header closeButton>Accept Booking</Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              The client offered{" "}
              <strong>${bookingItem?.offeredAmount ?? bookingItem?.grossAmount}</strong>
              . Accept at this amount or enter a different one.
            </p>

            <div className="form-group">
              <label>Amount</label>
              <div className="offered-amount-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  min={1}
                  step="0.01"
                  className="form-control"
                  placeholder="0.00"
                  value={acceptPrice.amount}
                  onChange={(e) =>
                    setAcceptPrice((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="action-btns-wrapper mt-3">
              <CustomButton
                isLoading={acceptLoading}
                title="Accept Booking"
                onClick={() => {
                  const amount = Number(acceptPrice.amount);
                  if (!amount || amount <= 0) {
                    showToast(
                      "error",
                      "Please enter an amount greater than $0."
                    );
                    return;
                  }
                  setAcceptPrice({ show: false, amount: "" });
                  acceptCancelBooking("accept", amount);
                }}
              />
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default BookingInfoPanel;
